import {SerializedError} from "@reduxjs/toolkit";
import {FetchBaseQueryError} from "@reduxjs/toolkit/query";

import {trackAmplitudeEvent} from "@pg-mono/amplitude";
import {gtmEcommerceOfferTypeMap, HomeMatchMode, OfferType} from "@pg-rp/common-types";
import {IPropertyDetailDTO} from "@pg-rp/homematch-tinder";

import {OFFER_LIST_CARD_CONTENT} from "../../../components/offer_list_card/constants/offer_list_card_content";
import {FAVORITES_LABELS} from "../../favorites/constants/favorites_labels";
import {View} from "../../km_app/types/View";
import {ApplicationSource} from "../../lead/types/ApplicationSource";
import {ApplicationSourceSection} from "../../lead/types/ApplicationSourceSection";
import {ERROR_NOTIFICATIONS_CONTENT} from "../../notifications/constants/error_notifications_content";
import {IPropertyListItemDTO} from "../../properties/types/IPropertyListItemDTO";
import {getGtmEcommerceCountry} from "../gtm/ecommerce/types/gtm_ecommerce_country_map";
import {GTMEcommerceItemStatus} from "../gtm/ecommerce/types/gtm_ecommerce_item_status";
import {getRtkQueryNonFieldErrorMessage} from "../utils/get_rtk_query_non_field_error_message";
import {AmplitudeEventPropertyValue, getAmplitudeItemIdProperties, getAmplitudePropertyTrackingIds} from "./get_amplitude_item_id_properties";
import {trackAmplitudePurchase} from "./track_amplitude_purchase";

export type AmplitudeLeadProperty = IPropertyListItemDTO | IPropertyDetailDTO;

/** Amplitude event property bag used in `generate_lead` / `lead_form_error` `products[]` (same shape as other KM Amplitude item helpers). */
type AmplitudeLeadProduct = Record<string, AmplitudeEventPropertyValue | boolean>;

/** DSA-A-59 / DSA-A-61 `form_location`. `km_favourite` (singular) is intentional — not `km_favourites` from view_item_list (DSA-A-71). */
enum AmplitudeLeadFormLocation {
    KM_FAVOURITE = "km_favourite",
    KM_OFFER_COMPARISON = "km_offer_comparison",
    KM_OFFER_DETAILS = "km_offer_details",
    HOMEMATCH = "homematch"
}

/** DSA-A-59 `event_action` — lead sent with logged-in account data (no manual form fill). */
export enum AmplitudeLeadEventAction {
    AUTOFILL = "autofill"
}

/** DSA-A-59 / DSA-A-61 `lead_type` for single-property KM leads. */
enum AmplitudeLeadType {
    SINGLE_LEAD = "single_lead"
}

/** DSA-A-61 `error_type` for API failures after CTA. */
enum AmplitudeLeadErrorType {
    SERVER = "server"
}

interface IAmplitudeLeadEntrypointContext {
    ctaText: string;
    formLocation: AmplitudeLeadFormLocation;
    source: ApplicationSource;
}

const VIEW_TO_LEAD_ENTRYPOINT_CONTEXT: Partial<Record<View, IAmplitudeLeadEntrypointContext>> = {
    [View.FAVORITE]: {
        ctaText: OFFER_LIST_CARD_CONTENT.ASK_OFFER_BUTTON,
        formLocation: AmplitudeLeadFormLocation.KM_FAVOURITE,
        source: ApplicationSource.FavouritesPropertyInquiry
    },
    [View.COMPARATOR]: {
        ctaText: FAVORITES_LABELS.COMPARATOR_ASK_OFFER_BUTTON,
        formLocation: AmplitudeLeadFormLocation.KM_OFFER_COMPARISON,
        source: ApplicationSource.Comparator
    }
};

const HOME_MATCH_MODE_TO_SOURCE: Record<HomeMatchMode, ApplicationSource> = {
    [HomeMatchMode.Basic]: ApplicationSource.HomematchBasic,
    [HomeMatchMode.Lite]: ApplicationSource.HomematchLite,
    [HomeMatchMode.Tinder]: ApplicationSource.HomematchTinder
};

const getRegionStatName = (regionStat?: {name: string} | string | null): string | undefined => {
    if (!regionStat) return undefined;

    return typeof regionStat === "string" ? regionStat : regionStat.name;
};

const getRegionCategoriesFromFullName = (fullName?: string | null) => {
    if (!fullName) return {item_category3: undefined, item_category4: undefined, item_category5: undefined};

    const parts = fullName
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);

    if (parts.length < 2) return {item_category3: undefined, item_category4: undefined, item_category5: undefined};

    return {
        item_category3: parts[0],
        item_category4: parts[1],
        item_category5: parts.length >= 3 ? parts[parts.length - 1] : undefined
    };
};

export class LeadAmplitudeTracker {
    static getApplicationSourceForView(viewType: View): ApplicationSource | undefined {
        return VIEW_TO_LEAD_ENTRYPOINT_CONTEXT[viewType]?.source;
    }

    private static getProducts(property: AmplitudeLeadProperty): AmplitudeLeadProduct[] {
        const offerType = (property.type ?? property.offer.type) as OfferType;

        const product: AmplitudeLeadProduct = {
            ...getAmplitudeItemIdProperties(getAmplitudePropertyTrackingIds(property)),
            item_name: property.offer.name,
            item_brand: property.offer.vendor.name,
            item_category: "property",
            item_category2: getGtmEcommerceCountry(property.region.country),
            item_type: gtmEcommerceOfferTypeMap[offerType],
            item_status: property.is_sold ? GTMEcommerceItemStatus.Archived : GTMEcommerceItemStatus.Active
        };

        const fromFullName = getRegionCategoriesFromFullName(property.region.full_name);

        const voivodeshipName = fromFullName.item_category3 ?? getRegionStatName(property.region.stats?.region_type_voivodeship);
        if (voivodeshipName) product.item_category3 = voivodeshipName;

        const cityName = fromFullName.item_category4 ?? getRegionStatName(property.region.stats?.region_type_city);
        if (cityName) product.item_category4 = cityName;

        const districtName = fromFullName.item_category5 ?? getRegionStatName(property.region.stats?.region_type_district);
        if (districtName) product.item_category5 = districtName;

        if (property.region.id != null) product.item_region_id = property.region.id;

        const commissioningDate = property.offer.construction_date_range?.upper;
        if (commissioningDate) product.item_comissioning_date = commissioningDate;

        if (property.price != null) product.price = property.price;

        product.variant_name = property.flat_number;

        if (property.area != null) product.variant_area = property.area;
        if (property.rooms != null) product.variant_rooms = property.rooms;

        const roomHeight = property.offer.stats?.ranges_height_max;
        if (roomHeight != null) product.variant_room_height = roomHeight;

        return [product];
    }

    private static getErrorDetails(
        error: FetchBaseQueryError | SerializedError | undefined,
        fallbackMessage: string = ERROR_NOTIFICATIONS_CONTENT.DEFAULT_API
    ): {errorCode: string; errorMessage: string} {
        if (error && "status" in error) {
            return {
                errorCode: String(error.status),
                errorMessage: getRtkQueryNonFieldErrorMessage(error) ?? fallbackMessage
            };
        }

        return {
            errorCode: error?.name ?? "unknown",
            errorMessage: error?.message ?? fallbackMessage
        };
    }

    private static generateLead(params: {
        ctaText: string;
        formLocation: AmplitudeLeadFormLocation;
        source: ApplicationSource;
        property: AmplitudeLeadProperty;
        transactionId: string;
        eventAction?: AmplitudeLeadEventAction;
    }) {
        const {ctaText, eventAction, formLocation, property, source, transactionId} = params;

        trackAmplitudeEvent("generate_lead", {
            transaction_id: transactionId,
            inquiry_source: source,
            source_id: source,
            cta_text: ctaText,
            form_location: formLocation,
            lead_type: AmplitudeLeadType.SINGLE_LEAD,
            ...(eventAction ? {event_action: eventAction} : {}),
            products: this.getProducts(property)
        });
    }

    private static leadFormError(params: {
        ctaText: string;
        formLocation: AmplitudeLeadFormLocation;
        source: ApplicationSource;
        property: AmplitudeLeadProperty;
        error: FetchBaseQueryError | SerializedError | undefined;
        fallbackMessage?: string;
    }) {
        const {ctaText, error, fallbackMessage, formLocation, property, source} = params;
        const {errorCode, errorMessage} = this.getErrorDetails(error, fallbackMessage);

        trackAmplitudeEvent("lead_form_error", {
            error_type: AmplitudeLeadErrorType.SERVER,
            error_message: errorMessage,
            error_code: errorCode,
            inquiry_source: source,
            source_id: source,
            cta_text: ctaText,
            form_location: formLocation,
            lead_type: AmplitudeLeadType.SINGLE_LEAD,
            products: this.getProducts(property)
        });
    }

    private static generateLeadForView(params: {
        viewType: View;
        property: AmplitudeLeadProperty;
        transactionId: string;
        eventAction?: AmplitudeLeadEventAction;
        ctaText?: string;
    }): boolean {
        const entrypoint = VIEW_TO_LEAD_ENTRYPOINT_CONTEXT[params.viewType];
        if (!entrypoint) return false;

        this.generateLead({
            ...entrypoint,
            ...(params.ctaText ? {ctaText: params.ctaText} : {}),
            eventAction: params.eventAction,
            property: params.property,
            transactionId: params.transactionId
        });

        return true;
    }

    static trackLeadError(params: {
        viewType: View;
        property: AmplitudeLeadProperty;
        error: FetchBaseQueryError | SerializedError | undefined;
        ctaText?: string;
    }) {
        const entrypoint = VIEW_TO_LEAD_ENTRYPOINT_CONTEXT[params.viewType];
        if (!entrypoint) return;

        this.leadFormError({
            ...entrypoint,
            ...(params.ctaText ? {ctaText: params.ctaText} : {}),
            error: params.error,
            property: params.property
        });
    }

    static trackLeadSent(params: {
        viewType: View;
        property: AmplitudeLeadProperty;
        transactionId: string;
        eventAction?: AmplitudeLeadEventAction;
        ctaText?: string;
        purchaseFallback: {
            sourceSection?: ApplicationSourceSection;
            leadOriginPayload?: {property?: number; offer?: number};
        };
    }) {
        const tracked = this.generateLeadForView({
            ctaText: params.ctaText,
            eventAction: params.eventAction,
            property: params.property,
            transactionId: params.transactionId,
            viewType: params.viewType
        });

        if (tracked) return;

        trackAmplitudePurchase({
            leadOriginPayload: params.purchaseFallback.leadOriginPayload,
            leadUuid: params.transactionId,
            property: params.property,
            sourceSection: params.purchaseFallback.sourceSection,
            viewType: params.viewType
        });
    }

    static trackLeadSentForHomeMatch(params: {
        property: AmplitudeLeadProperty;
        mode: HomeMatchMode;
        ctaText: string;
        /**
         * DSA-A-59 expects a backend lead UUID; accept-prelead returns none (Application is created async).
         * Callers pass a stable synthetic id until BE exposes the Application uuid.
         */
        transactionId: string;
    }) {
        this.generateLead({
            ctaText: params.ctaText,
            eventAction: AmplitudeLeadEventAction.AUTOFILL,
            formLocation: AmplitudeLeadFormLocation.HOMEMATCH,
            property: params.property,
            source: HOME_MATCH_MODE_TO_SOURCE[params.mode],
            transactionId: params.transactionId
        });
    }

    static trackLeadSentForRecommendedProperties(params: {property: AmplitudeLeadProperty; preleadId: number; ctaText: string}) {
        this.generateLead({
            ctaText: params.ctaText,
            eventAction: AmplitudeLeadEventAction.AUTOFILL,
            formLocation: AmplitudeLeadFormLocation.KM_OFFER_DETAILS,
            property: params.property,
            source: ApplicationSource.HomematchRecommended,
            transactionId: `hm-recommended-prelead-${params.preleadId}`
        });
    }

    static trackLeadErrorForRecommendedProperties(params: {
        property: AmplitudeLeadProperty;
        ctaText: string;
        error: FetchBaseQueryError | SerializedError | undefined;
        fallbackMessage: string;
    }) {
        this.leadFormError({
            ctaText: params.ctaText,
            error: params.error,
            fallbackMessage: params.fallbackMessage,
            formLocation: AmplitudeLeadFormLocation.KM_OFFER_DETAILS,
            property: params.property,
            source: ApplicationSource.HomematchRecommended
        });
    }

    static trackLeadErrorForHomeMatch(params: {
        property: AmplitudeLeadProperty;
        mode: HomeMatchMode;
        ctaText: string;
        error: FetchBaseQueryError | SerializedError | undefined;
        fallbackMessage: string;
    }) {
        this.leadFormError({
            ctaText: params.ctaText,
            error: params.error,
            fallbackMessage: params.fallbackMessage,
            formLocation: AmplitudeLeadFormLocation.HOMEMATCH,
            property: params.property,
            source: HOME_MATCH_MODE_TO_SOURCE[params.mode]
        });
    }
}
import {SerializedError} from "@reduxjs/toolkit";
import {FetchBaseQueryError} from "@reduxjs/toolkit/query";

import {trackAmplitudeEvent} from "@pg-mono/amplitude";
import {gtmEcommerceOfferTypeMap, HomeMatchMode, OfferType} from "@pg-rp/common-types";
import {IPropertyDetailDTO} from "@pg-rp/homematch-tinder";

import {OFFER_LIST_CARD_CONTENT} from "../../../components/offer_list_card/constants/offer_list_card_content";
import {FAVORITES_LABELS} from "../../favorites/constants/favorites_labels";
import {View} from "../../km_app/types/View";
import {ApplicationSource} from "../../lead/types/ApplicationSource";
import {ApplicationSourceSection} from "../../lead/types/ApplicationSourceSection";
import {ERROR_NOTIFICATIONS_CONTENT} from "../../notifications/constants/error_notifications_content";
import {IPropertyListItemDTO} from "../../properties/types/IPropertyListItemDTO";
import {getGtmEcommerceCountry} from "../gtm/ecommerce/types/gtm_ecommerce_country_map";
import {GTMEcommerceItemStatus} from "../gtm/ecommerce/types/gtm_ecommerce_item_status";
import {getRtkQueryNonFieldErrorMessage} from "../utils/get_rtk_query_non_field_error_message";
import {AmplitudeEventPropertyValue, getAmplitudeItemIdProperties, getAmplitudePropertyTrackingIds} from "./get_amplitude_item_id_properties";
import {trackAmplitudePurchase} from "./track_amplitude_purchase";

export type AmplitudeLeadProperty = IPropertyListItemDTO | IPropertyDetailDTO;

/** Amplitude event property bag used in `generate_lead` / `lead_form_error` `products[]` (same shape as other KM Amplitude item helpers). */
type AmplitudeLeadProduct = Record<string, AmplitudeEventPropertyValue | boolean>;

/** DSA-A-59 / DSA-A-61 `form_location`. `km_favourite` (singular) is intentional — not `km_favourites` from view_item_list (DSA-A-71). */
enum AmplitudeLeadFormLocation {
    KM_FAVOURITE = "km_favourite",
    KM_OFFER_COMPARISON = "km_offer_comparison",
    KM_OFFER_DETAILS = "km_offer_details",
    HOMEMATCH = "homematch"
}

/** DSA-A-59 `event_action` — lead sent with logged-in account data (no manual form fill). */
export enum AmplitudeLeadEventAction {
    AUTOFILL = "autofill"
}

/** DSA-A-59 / DSA-A-61 `lead_type` for single-property KM leads. */
enum AmplitudeLeadType {
    SINGLE_LEAD = "single_lead"
}

/** DSA-A-61 `error_type` for API failures after CTA. */
enum AmplitudeLeadErrorType {
    SERVER = "server"
}

interface IAmplitudeLeadEntrypointContext {
    ctaText: string;
    formLocation: AmplitudeLeadFormLocation;
    source: ApplicationSource;
}

const VIEW_TO_LEAD_ENTRYPOINT_CONTEXT: Partial<Record<View, IAmplitudeLeadEntrypointContext>> = {
    [View.FAVORITE]: {
        ctaText: OFFER_LIST_CARD_CONTENT.ASK_OFFER_BUTTON,
        formLocation: AmplitudeLeadFormLocation.KM_FAVOURITE,
        source: ApplicationSource.FavouritesPropertyInquiry
    },
    [View.COMPARATOR]: {
        ctaText: FAVORITES_LABELS.COMPARATOR_ASK_OFFER_BUTTON,
        formLocation: AmplitudeLeadFormLocation.KM_OFFER_COMPARISON,
        source: ApplicationSource.Comparator
    }
};

const HOME_MATCH_MODE_TO_SOURCE: Record<HomeMatchMode, ApplicationSource> = {
    [HomeMatchMode.Basic]: ApplicationSource.HomematchBasic,
    [HomeMatchMode.Lite]: ApplicationSource.HomematchLite,
    [HomeMatchMode.Tinder]: ApplicationSource.HomematchTinder
};

const getRegionStatName = (regionStat?: {name: string} | string | null): string | undefined => {
    if (!regionStat) return undefined;

    return typeof regionStat === "string" ? regionStat : regionStat.name;
};

const getRegionCategoriesFromFullName = (fullName?: string | null) => {
    if (!fullName) return {item_category3: undefined, item_category4: undefined, item_category5: undefined};

    const parts = fullName
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);

    if (parts.length < 2) return {item_category3: undefined, item_category4: undefined, item_category5: undefined};

    return {
        item_category3: parts[0],
        item_category4: parts[1],
        item_category5: parts.length >= 3 ? parts[parts.length - 1] : undefined
    };
};

export class LeadAmplitudeTracker {
    static getApplicationSourceForView(viewType: View): ApplicationSource | undefined {
        return VIEW_TO_LEAD_ENTRYPOINT_CONTEXT[viewType]?.source;
    }

    private static getProducts(property: AmplitudeLeadProperty): AmplitudeLeadProduct[] {
        const offerType = (property.type ?? property.offer.type) as OfferType;

        const product: AmplitudeLeadProduct = {
            ...getAmplitudeItemIdProperties(getAmplitudePropertyTrackingIds(property)),
            item_name: property.offer.name,
            item_brand: property.offer.vendor.name,
            item_category: "property",
            item_category2: getGtmEcommerceCountry(property.region.country),
            item_type: gtmEcommerceOfferTypeMap[offerType],
            item_status: property.is_sold ? GTMEcommerceItemStatus.Archived : GTMEcommerceItemStatus.Active
        };

        const fromFullName = getRegionCategoriesFromFullName(property.region.full_name);

        const voivodeshipName = fromFullName.item_category3 ?? getRegionStatName(property.region.stats?.region_type_voivodeship);
        if (voivodeshipName) product.item_category3 = voivodeshipName;

        const cityName = fromFullName.item_category4 ?? getRegionStatName(property.region.stats?.region_type_city);
        if (cityName) product.item_category4 = cityName;

        const districtName = fromFullName.item_category5 ?? getRegionStatName(property.region.stats?.region_type_district);
        if (districtName) product.item_category5 = districtName;

        if (property.region.id != null) product.item_region_id = property.region.id;

        const commissioningDate = property.offer.construction_date_range?.upper;
        if (commissioningDate) product.item_comissioning_date = commissioningDate;

        if (property.price != null) product.price = property.price;

        product.variant_name = property.flat_number;

        if (property.area != null) product.variant_area = property.area;
        if (property.rooms != null) product.variant_rooms = property.rooms;

        const roomHeight = property.offer.stats?.ranges_height_max;
        if (roomHeight != null) product.variant_room_height = roomHeight;

        return [product];
    }

    private static getErrorDetails(
        error: FetchBaseQueryError | SerializedError | undefined,
        fallbackMessage: string = ERROR_NOTIFICATIONS_CONTENT.DEFAULT_API
    ): {errorCode: string; errorMessage: string} {
        if (error && "status" in error) {
            return {
                errorCode: String(error.status),
                errorMessage: getRtkQueryNonFieldErrorMessage(error) ?? fallbackMessage
            };
        }

        return {
            errorCode: error?.name ?? "unknown",
            errorMessage: error?.message ?? fallbackMessage
        };
    }

    private static generateLead(params: {
        ctaText: string;
        formLocation: AmplitudeLeadFormLocation;
        source: ApplicationSource;
        property: AmplitudeLeadProperty;
        transactionId: string;
        eventAction?: AmplitudeLeadEventAction;
    }) {
        const {ctaText, eventAction, formLocation, property, source, transactionId} = params;

        trackAmplitudeEvent("generate_lead", {
            transaction_id: transactionId,
            inquiry_source: source,
            source_id: source,
            cta_text: ctaText,
            form_location: formLocation,
            lead_type: AmplitudeLeadType.SINGLE_LEAD,
            ...(eventAction ? {event_action: eventAction} : {}),
            products: this.getProducts(property)
        });
    }

    private static leadFormError(params: {
        ctaText: string;
        formLocation: AmplitudeLeadFormLocation;
        source: ApplicationSource;
        property: AmplitudeLeadProperty;
        error: FetchBaseQueryError | SerializedError | undefined;
        fallbackMessage?: string;
    }) {
        const {ctaText, error, fallbackMessage, formLocation, property, source} = params;
        const {errorCode, errorMessage} = this.getErrorDetails(error, fallbackMessage);

        trackAmplitudeEvent("lead_form_error", {
            error_type: AmplitudeLeadErrorType.SERVER,
            error_message: errorMessage,
            error_code: errorCode,
            inquiry_source: source,
            source_id: source,
            cta_text: ctaText,
            form_location: formLocation,
            lead_type: AmplitudeLeadType.SINGLE_LEAD,
            products: this.getProducts(property)
        });
    }

    private static generateLeadForView(params: {
        viewType: View;
        property: AmplitudeLeadProperty;
        transactionId: string;
        eventAction?: AmplitudeLeadEventAction;
        ctaText?: string;
    }): boolean {
        const entrypoint = VIEW_TO_LEAD_ENTRYPOINT_CONTEXT[params.viewType];
        if (!entrypoint) return false;

        this.generateLead({
            ...entrypoint,
            ...(params.ctaText ? {ctaText: params.ctaText} : {}),
            eventAction: params.eventAction,
            property: params.property,
            transactionId: params.transactionId
        });

        return true;
    }

    static trackLeadError(params: {
        viewType: View;
        property: AmplitudeLeadProperty;
        error: FetchBaseQueryError | SerializedError | undefined;
        ctaText?: string;
    }) {
        const entrypoint = VIEW_TO_LEAD_ENTRYPOINT_CONTEXT[params.viewType];
        if (!entrypoint) return;

        this.leadFormError({
            ...entrypoint,
            ...(params.ctaText ? {ctaText: params.ctaText} : {}),
            error: params.error,
            property: params.property
        });
    }

    static trackLeadSent(params: {
        viewType: View;
        property: AmplitudeLeadProperty;
        transactionId: string;
        eventAction?: AmplitudeLeadEventAction;
        ctaText?: string;
        purchaseFallback: {
            sourceSection?: ApplicationSourceSection;
            leadOriginPayload?: {property?: number; offer?: number};
        };
    }) {
        const tracked = this.generateLeadForView({
            ctaText: params.ctaText,
            eventAction: params.eventAction,
            property: params.property,
            transactionId: params.transactionId,
            viewType: params.viewType
        });

        if (tracked) return;

        trackAmplitudePurchase({
            leadOriginPayload: params.purchaseFallback.leadOriginPayload,
            leadUuid: params.transactionId,
            property: params.property,
            sourceSection: params.purchaseFallback.sourceSection,
            viewType: params.viewType
        });
    }

    static trackLeadSentForHomeMatch(params: {
        property: AmplitudeLeadProperty;
        mode: HomeMatchMode;
        ctaText: string;
        /**
         * DSA-A-59 expects a backend lead UUID; accept-prelead returns none (Application is created async).
         * Callers pass a stable synthetic id until BE exposes the Application uuid.
         */
        transactionId: string;
    }) {
        this.generateLead({
            ctaText: params.ctaText,
            eventAction: AmplitudeLeadEventAction.AUTOFILL,
            formLocation: AmplitudeLeadFormLocation.HOMEMATCH,
            property: params.property,
            source: HOME_MATCH_MODE_TO_SOURCE[params.mode],
            transactionId: params.transactionId
        });
    }

    static trackLeadSentForRecommendedProperties(params: {property: AmplitudeLeadProperty; preleadId: number; ctaText: string}) {
        this.generateLead({
            ctaText: params.ctaText,
            eventAction: AmplitudeLeadEventAction.AUTOFILL,
            formLocation: AmplitudeLeadFormLocation.KM_OFFER_DETAILS,
            property: params.property,
            source: ApplicationSource.HomematchRecommended,
            transactionId: `hm-recommended-prelead-${params.preleadId}`
        });
    }

    static trackLeadErrorForRecommendedProperties(params: {
        property: AmplitudeLeadProperty;
        ctaText: string;
        error: FetchBaseQueryError | SerializedError | undefined;
        fallbackMessage: string;
    }) {
        this.leadFormError({
            ctaText: params.ctaText,
            error: params.error,
            fallbackMessage: params.fallbackMessage,
            formLocation: AmplitudeLeadFormLocation.KM_OFFER_DETAILS,
            property: params.property,
            source: ApplicationSource.HomematchRecommended
        });
    }

    static trackLeadErrorForHomeMatch(params: {
        property: AmplitudeLeadProperty;
        mode: HomeMatchMode;
        ctaText: string;
        error: FetchBaseQueryError | SerializedError | undefined;
        fallbackMessage: string;
    }) {
        this.leadFormError({
            ctaText: params.ctaText,
            error: params.error,
            fallbackMessage: params.fallbackMessage,
            formLocation: AmplitudeLeadFormLocation.HOMEMATCH,
            property: params.property,
            source: HOME_MATCH_MODE_TO_SOURCE[params.mode]
        });
    }
}
